import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Logo } from "./Logo";
import { FlagIT, FlagEN } from "./Flags";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";

export default function Header() {
  const { t, lang, setLang } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [prodOpen, setProdOpen] = useState(false);
  const prodCloseTimer = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setProdOpen(false);
  }, [location.pathname]);

  const links = [
    { to: "/", label: t("nav.home"), testId: "nav-home-link" },
    { to: "/scienza-qualita", label: t("nav.science"), testId: "nav-science-link" },
    { to: "/chi-siamo", label: t("nav.about"), testId: "nav-about-link" },
    { to: "/journal", label: t("nav.journal"), testId: "nav-journal-link" },
    { to: "/faq", label: t("nav.faq"), testId: "nav-faq-link" },
    { to: "/professionisti", label: t("nav.pro"), testId: "nav-pro-link" },
    { to: "/contatti", label: t("nav.contact"), testId: "nav-contact-link" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[rgba(249,248,246,0.85)] border-b border-soft"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <Logo />

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8" aria-label="Main">
          <NavLink to="/" end className={navLinkClass} data-testid="nav-home-link">
            {t("nav.home")}
          </NavLink>

          {/* Products dropdown */}
          <div
            className="relative"
            onMouseEnter={() => {
              if (prodCloseTimer.current) clearTimeout(prodCloseTimer.current);
              setProdOpen(true);
            }}
            onMouseLeave={() => {
              prodCloseTimer.current = setTimeout(() => setProdOpen(false), 180);
            }}
          >
            <button
              data-testid="nav-products-trigger"
              className="inline-flex items-center gap-1 text-sm text-brand hover:opacity-70 transition-opacity"
              onClick={() => setProdOpen((v) => !v)}
              aria-expanded={prodOpen}
              aria-haspopup="menu"
            >
              {t("nav.products")}
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {prodOpen && (
              <div
                role="menu"
                className="absolute top-full left-1/2 -translate-x-1/2 pt-3 min-w-[220px]"
              >
                <div className="bg-white border border-soft shadow-[0_20px_50px_-20px_rgba(26,54,54,0.15)]">
                  <Link
                    to="/prodotti?linea=pet"
                    data-testid="nav-pet-line-link"
                    className="block px-5 py-3 text-sm hover:bg-cream-warm transition-colors"
                  >
                    <span className="text-pet mr-2">•</span>{t("nav.pet")}
                  </Link>
                  <Link
                    to="/linea-human"
                    data-testid="nav-human-line-link"
                    className="block px-5 py-3 text-sm hover:bg-cream-warm transition-colors border-t border-soft"
                  >
                    <span className="text-human mr-2">•</span>{t("nav.human")}
                  </Link>
                  <Link
                    to="/prodotti"
                    className="block px-5 py-3 text-xs uppercase tracking-editorial text-muted-ink hover:bg-cream-warm transition-colors border-t border-soft"
                  >
                    {t("common.view_all")}
                  </Link>
                </div>
              </div>
            )}
          </div>

          {links.slice(1).map((l) => (
            <NavLink key={l.to} to={l.to} className={navLinkClass} data-testid={l.testId}>
              {l.label}
            </NavLink>
          ))}
          <a
            href="/area-b2b"
            data-testid="nav-b2b-link"
            className="text-sm text-brand transition-opacity hover:opacity-70 opacity-80 border-l border-soft pl-6 ml-1"
          >
            Area B2B
          </a>
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <LangSwitcher lang={lang} setLang={setLang} />
          <Button
            asChild
            data-testid="header-cta-products"
            className="rounded-full bg-brand text-white hover:bg-[#0f2020] px-5"
          >
            <Link to="/prodotti">{t("nav.shop_cta")}</Link>
          </Button>
        </div>

        {/* Mobile toggle */}
        <button
          className="lg:hidden inline-flex items-center justify-center h-10 w-10 text-brand"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-soft bg-[rgba(249,248,246,0.98)] backdrop-blur-xl"
        >
          <nav className="px-6 py-5 flex flex-col gap-1">
            <MobileLink to="/" label={t("nav.home")} testId="mobile-home" />
            <MobileLink to="/prodotti?linea=pet" label={t("nav.pet")} accent="pet" testId="mobile-pet" />
            <MobileLink to="/linea-human" label={t("nav.human")} accent="human" testId="mobile-human" />
            <MobileLink to="/scienza-qualita" label={t("nav.science")} />
            <MobileLink to="/chi-siamo" label={t("nav.about")} />
            <MobileLink to="/journal" label={t("nav.journal")} />
            <MobileLink to="/faq" label={t("nav.faq")} />
            <MobileLink to="/professionisti" label={t("nav.pro")} />
            <MobileLink to="/contatti" label={t("nav.contact")} />
            <MobileLink to="/area-b2b" label="Area B2B" testId="mobile-b2b" />
            <div className="pt-4 mt-2 border-t border-soft flex items-center justify-between">
              <LangSwitcher lang={lang} setLang={setLang} />
              <Button
                asChild
                data-testid="mobile-cta-products"
                className="rounded-full bg-brand text-white hover:bg-[#0f2020]"
              >
                <Link to="/prodotti">{t("nav.shop_cta")}</Link>
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

const navLinkClass = ({ isActive }) =>
  `text-sm text-brand transition-opacity hover:opacity-70 ${
    isActive ? "opacity-100 border-b border-brand pb-0.5" : "opacity-80"
  }`;

function MobileLink({ to, label, testId, accent }) {
  const dot =
    accent === "pet"
      ? "before:bg-pet"
      : accent === "human"
      ? "before:bg-human"
      : "before:bg-transparent";
  return (
    <NavLink
      to={to}
      data-testid={testId}
      className={`py-3 text-base text-brand border-b border-soft last:border-b-0 flex items-center gap-3 before:content-[''] before:h-1.5 before:w-1.5 before:rounded-full ${dot}`}
    >
      {label}
    </NavLink>
  );
}

function LangSwitcher({ lang, setLang }) {
  return (
    <div
      className="inline-flex items-center gap-1.5 text-xs uppercase tracking-editorial text-brand"
      role="group"
      aria-label="Language switcher"
    >
      <button
        data-testid="lang-switch-it"
        aria-label="Italiano"
        aria-pressed={lang === "it"}
        className={`inline-flex items-center gap-1 px-1.5 py-1 transition-opacity ${
          lang === "it" ? "opacity-100" : "opacity-50 hover:opacity-100"
        }`}
        onClick={() => setLang("it")}
      >
        <FlagIT size={18} />
        <span className="text-[11px]">IT</span>
      </button>
      <span className="opacity-25">·</span>
      <button
        data-testid="lang-switch-en"
        aria-label="English"
        aria-pressed={lang === "en"}
        className={`inline-flex items-center gap-1 px-1.5 py-1 transition-opacity ${
          lang === "en" ? "opacity-100" : "opacity-50 hover:opacity-100"
        }`}
        onClick={() => setLang("en")}
      >
        <FlagEN size={18} />
        <span className="text-[11px]">EN</span>
      </button>
    </div>
  );
}
